import { useState } from "react";
import { Badge, BottomNavigation, BottomNavigationAction, Box } from "@mui/material";
import {
  Dashboard as DashboardIcon,
  QueryStats as ResearchIcon,
  Psychology as InsightAiIcon,
  CalendarToday as EarningsIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { useKeyboardVisible } from "../../hooks/useKeyboardVisible";
import { useSafeNavigate } from "../../hooks/useSafeNavigate";
import { hapticSelection } from "../../lib/haptics";
import { markNavStart } from "../../lib/navPerf";
import { useRecentNotificationCount } from "../Notifications/hooks/useRecentNotificationCount";
import MoreSheet from "./MoreSheet";

import {
  BOTTOM_TAB_BAR_HEIGHT,
  BOTTOM_TAB_BAR_SAFE_AREA,
} from "./bottomTabBarLayout";

export { BOTTOM_TAB_BAR_HEIGHT, BOTTOM_TAB_BAR_SAFE_AREA };

const TABS = [
  { label: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
  // "Research", not "Charts": this is where users look companies up — the
  // old label undersold it and pointed users away from the core surface.
  { label: "Research", icon: <ResearchIcon />, path: "/charts" },
  { label: "Insight AI", icon: <InsightAiIcon />, path: "/chat" },
  { label: "Earnings", icon: <EarningsIcon />, path: "/earnings" },
];

/**
 * Native app primary navigation. The four highest-traffic destinations get
 * tabs; everything else (Compare, Insiders, Projections, account actions)
 * lives in the More sheet — the app's bottom-sheet idiom, not the web-era
 * side drawer (which remains the mobile WEB surface, opened via the burger).
 */
const BottomTabBar = () => {
  const navigate = useSafeNavigate();
  const location = useLocation();
  const keyboardVisible = useKeyboardVisible();
  const [moreOpen, setMoreOpen] = useState(false);
  const hasRecentNotifications = useRecentNotificationCount() > 0;

  const selectedPath = TABS.find((tab) => location.pathname.startsWith(tab.path))?.path ?? null;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        // Tab bars belong under the keyboard, not hovering on top of it —
        // slide away while typing (keyboard resize otherwise pins it above).
        transform: keyboardVisible ? "translateY(100%)" : "translateY(0)",
        transition: "transform 0.2s ease",
        paddingBottom: BOTTOM_TAB_BAR_SAFE_AREA,
        backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff"),
        borderTop: (theme) =>
          theme.palette.mode === "dark"
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.1)",
      }}
    >
      <BottomNavigation
        value={selectedPath}
        showLabels
        sx={{
          height: BOTTOM_TAB_BAR_HEIGHT,
          backgroundColor: "transparent",
          "& .MuiBottomNavigationAction-root": {
            color: (theme) =>
              theme.palette.mode === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
            minWidth: 0,
            // Instant pressed feedback: MUI suppresses the native tap flash
            // and delays its ripple 80ms, which reads as a dead tap.
            WebkitTapHighlightColor: "transparent",
            transition: "transform 0.1s ease",
            "&:active": {
              transform: "scale(0.9)",
            },
            "&.Mui-selected": {
              color: "#22c55e",
            },
          },
          "& .MuiBottomNavigationAction-label": {
            fontSize: "0.7rem",
            "&.Mui-selected": { fontSize: "0.7rem", fontWeight: 600 },
          },
        }}
      >
        {TABS.map((tab) => (
          <BottomNavigationAction
            key={tab.path}
            label={tab.label}
            icon={tab.icon}
            value={tab.path}
            onClick={() => {
              hapticSelection();
              markNavStart("tab_bar");
              navigate(tab.path);
            }}
          />
        ))}
        <BottomNavigationAction
          label="More"
          // Notifications live in the More sheet, so its tab carries the dot.
          icon={
            <Badge variant="dot" color="primary" invisible={!hasRecentNotifications}>
              <MenuIcon />
            </Badge>
          }
          onClick={() => {
            // The haptic previously came from toggleMobileDrawer; the sheet's
            // rows fire their own, so this is the only tick for the tap.
            hapticSelection();
            setMoreOpen(true);
          }}
        />
      </BottomNavigation>

      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
    </Box>
  );
};

export default BottomTabBar;
